import React from "react";
import {
  Check, Clock, Copy, FileAudio, PhoneIncoming, PhoneMissed, PhoneOutgoing, Trash2, Volume2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import CdrDateTimeCell from "@/components/cdr/CdrDateTimeCell";
import AgentPhoneChip from "@/components/common/AgentPhoneChip";
import { formatCdrStagingText } from "@/utils/cdrStagingBasket";
import { buildPublicRecordingUrl } from "@/utils/publicRecordingUrl";
import { toast } from "sonner";

const formatDuration = (seconds) => {
  const value = Number(seconds);
  if (!Number.isFinite(value) || value <= 0) return "0:00";
  const mins = Math.floor(value / 60);
  const secs = Math.floor(value % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const CdrStagingStatusBadge = ({ durum, durum_aciklama, isDark }) => {
  const getClass = () => {
    if (String(durum) === "1") return isDark ? "status-cevaplanan" : "bg-emerald-100 text-emerald-700 ring-emerald-200";
    if (String(durum) === "3") return isDark ? "status-mesgul" : "bg-rose-100 text-rose-700 ring-rose-200";
    return isDark ? "status-cevap-yok" : "bg-amber-100 text-amber-700 ring-amber-200";
  };
  const label = durum_aciklama === "cevaplanan"
    ? "Cevaplandı"
    : durum_aciklama === "mesgul"
      ? "Meşgul"
      : "Cevapsız";
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ring-1 ${getClass()}`}>
      {label}
    </span>
  );
};

const CdrStagingDirectionCell = ({ record, isDark, labelClass }) => {
  if (String(record.durum) !== "1") {
    return (
      <Badge className={isDark ? "bg-red-500/20 text-red-400 border border-red-500/30" : "bg-red-100 text-red-700 border border-red-200"}>
        <PhoneMissed className="w-3 h-3 mr-1" />
        {record.yon === 1 ? "Gelen" : record.yon === 2 ? "Giden" : "-"}
      </Badge>
    );
  }
  if (record.yon === 1) {
    return (
      <Badge className={isDark ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" : "bg-emerald-100 text-emerald-700 border border-emerald-200"}>
        <PhoneIncoming className="w-3 h-3 mr-1" />Gelen
      </Badge>
    );
  }
  if (record.yon === 2) {
    return (
      <Badge className={isDark ? "bg-blue-500/20 text-blue-400 border border-blue-500/30" : "bg-blue-100 text-blue-700 border border-blue-200"}>
        <PhoneOutgoing className="w-3 h-3 mr-1" />Giden
      </Badge>
    );
  }
  return <span className={labelClass}>-</span>;
};

const CdrStagingRecordsList = ({
  items,
  isDark,
  labelClass,
  textClass,
  selectedId = null,
  onSelect,
  onRemove,
  onPlay,
  onApplyRecordingUrl,
  applyRecordingLabel = "Ses URL'sini kopyala ve alana ekle",
  selectable = false,
  compact = false,
}) => {
  const headClass = `font-semibold whitespace-nowrap ${isDark ? "text-slate-300" : "text-gray-700"}`;
  const headerRowClass = isDark ? "border-slate-700 bg-slate-800/50" : "border-gray-200 bg-gray-50";

  if (!items.length) {
    return (
      <p className={`text-sm text-center py-16 ${labelClass}`}>
        Sepette çağrı kaydı yok. Ana sayfadaki CDR tablosundan sepet simgesine basarak ekleyin.
      </p>
    );
  }

  const handleRowClick = (row) => {
    if (selectable) onSelect?.(row);
  };

  const handleApplyRecording = async (row, e) => {
    e?.stopPropagation?.();
    const url = String(
      row.public_recording_url || buildPublicRecordingUrl(row.id) || row.recording || ""
    ).trim();
    if (!url) {
      toast.warning("Bu kayıtta ses URL'si yok");
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      /* clipboard optional */
    }
    if (onApplyRecordingUrl) {
      onApplyRecordingUrl(url, row);
      return;
    }
    toast.success("Ses kaydı URL'si kopyalandı");
  };

  return (
    <div className={`overflow-x-auto rounded-lg border ${isDark ? "border-slate-800" : "border-gray-200"}`}>
      <Table>
        <TableHeader>
          <TableRow className={headerRowClass}>
            {selectable && (
              <TableHead className={`w-10 ${headClass}`} aria-hidden="true" />
            )}
            <TableHead className={headClass}>Tarih</TableHead>
            <TableHead className={headClass}>Yön</TableHead>
            {!compact && <TableHead className={headClass}>Agent</TableHead>}
            <TableHead className={headClass}>Arayan</TableHead>
            <TableHead className={headClass}>Aranan</TableHead>
            <TableHead className={headClass}>Süre</TableHead>
            {!compact && <TableHead className={headClass}>Durum</TableHead>}
            <TableHead className={headClass}>İşlemler</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((row, i) => {
            const active = selectable && selectedId != null && Number(selectedId) === Number(row.id);
            return (
              <TableRow
                key={row.id}
                onClick={selectable ? () => handleRowClick(row) : undefined}
                className={`transition-colors ${
                  isDark ? "border-slate-800/50 hover:bg-slate-800/40" : "border-gray-100 hover:bg-blue-50/50"
                } ${
                  i % 2 === 0
                    ? (isDark ? "bg-slate-900/30" : "bg-white")
                    : (isDark ? "bg-slate-800/20" : "bg-gray-50/50")
                } ${
                  active
                    ? (isDark ? "ring-1 ring-inset ring-violet-500/50 bg-violet-500/10" : "ring-1 ring-inset ring-violet-300 bg-violet-50/80")
                    : ""
                } ${selectable ? "cursor-pointer" : ""}`}
              >
                {selectable && (
                  <TableCell className="w-10 text-center">
                    {active ? <Check className="w-4 h-4 text-violet-500 mx-auto" /> : null}
                  </TableCell>
                )}
                <TableCell className="whitespace-nowrap">
                  <CdrDateTimeCell value={row.tarih} isDark={isDark} />
                </TableCell>
                <TableCell>
                  <CdrStagingDirectionCell record={row} isDark={isDark} labelClass={labelClass} />
                </TableCell>
                {!compact && (
                  <TableCell className={`text-sm max-w-[140px] truncate ${isDark ? "text-slate-300" : "text-gray-700"}`} title={row.agent_name || undefined}>
                    {row.agent_name || "-"}
                  </TableCell>
                )}
                <TableCell className={`font-mono text-sm ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                  <AgentPhoneChip phone={row.arayan_numara} isDark={isDark} />
                </TableCell>
                <TableCell className={`font-mono text-sm ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                  <AgentPhoneChip phone={row.aranan_numara} isDark={isDark} />
                </TableCell>
                <TableCell>
                  <span className={`inline-flex items-center px-2 py-1 rounded font-mono text-sm ${
                    row.konusma_suresi > 0
                      ? (isDark ? "bg-purple-500/20 text-purple-400" : "bg-purple-100 text-purple-700")
                      : (isDark ? "text-slate-500" : "text-gray-400")
                  }`}>
                    <Clock className="w-3 h-3 mr-1" />
                    {formatDuration(row.konusma_suresi)}
                  </span>
                </TableCell>
                {!compact && (
                  <TableCell>
                    <CdrStagingStatusBadge durum={row.durum} durum_aciklama={row.durum_aciklama} isDark={isDark} />
                  </TableCell>
                )}
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-0.5">
                    {row.recording && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className={`p-1.5 h-auto rounded-lg ${
                          onApplyRecordingUrl
                            ? (isDark ? "text-violet-400 hover:text-violet-300 hover:bg-violet-500/10" : "text-violet-600 hover:text-violet-700 hover:bg-violet-50")
                            : (isDark ? "text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10" : "text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50")
                        }`}
                        title={onApplyRecordingUrl ? applyRecordingLabel : "Ses kaydı URL'sini kopyala"}
                        onClick={(e) => handleApplyRecording(row, e)}
                      >
                        <FileAudio className="w-4 h-4" />
                      </Button>
                    )}
                    {row.recording && onPlay && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className={`p-1.5 h-auto rounded-lg ${isDark ? "text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10" : "text-blue-600 hover:text-blue-700 hover:bg-blue-50"}`}
                        title="Dinle"
                        onClick={() => onPlay(row.public_recording_url || row.recording || buildPublicRecordingUrl(row.id), row)}
                      >
                        <Volume2 className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className={`p-1.5 h-auto rounded-lg ${isDark ? "text-slate-400 hover:text-slate-200 hover:bg-slate-800" : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"}`}
                      title="Bilgileri kopyala"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(formatCdrStagingText(row));
                          toast.success("CDR bilgileri kopyalandı");
                        } catch {
                          toast.error("Kopyalanamadı");
                        }
                      }}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    {!selectable && onRemove && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className={`p-1.5 h-auto rounded-lg ${isDark ? "text-rose-400 hover:bg-rose-500/10" : "text-rose-600 hover:bg-rose-50"}`}
                        title="Sepetten çıkar"
                        onClick={() => onRemove(row.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default CdrStagingRecordsList;
